import React, { useEffect, useRef, useState } from 'react';
import {
    View,
    Heading,
    Text,
    colors,
    GridColumn,
    Link,
} from '@fairygodboss/ui';
import { Author } from '../../../SharedComponents/ContentBody/Author';
import { Convincer } from '../../../SharedComponents/ContentBody/Convincer';
import ResponsivePicture from '../../../../../Elements/ResponsivePicture/ResponsivePicture';
import { useCommunityContent } from '../../CommunityContentContext';
import { AudioPlayer } from './AudioPlayer';

export const breakpoints = {
    desktop: {
        width: 669,
        transforms: {
            quality: 'auto',
            crop: 'scale',
            format: 'auto',
        },
    },
    tablet: {
        width: 750,
        transforms: {
            quality: 'auto',
            crop: 'scale',
            format: 'auto',
        },
    },
    smallTablet: {
        width: 665,
        transforms: {
            quality: 'auto',
            crop: 'scale',
            format: 'auto',
        },
    },
    mobile: {
        width: 567,
        transforms: {
            quality: 'auto',
            crop: 'scale',
            format: 'auto',
        },
    },
};

export const ContentInfo = () => {
    const {
        contentImage,
        author,
        contentModifiedDate,
        paidCompany,
        isSponsor,
        showSponsoredBy,
        windowWidth,
        topConvincerRef,
        contentDescription,
        hash,
        type,
        backgroundHeight,
        isLoggedIn,
        handleResize,
        articlePictureRef,
        audioSrc,
        content,
    } = useCommunityContent();
    const [authorPaddingTop, setAuthorPaddingTop] = useState(28);
    const { alt, credit, src } = contentImage;
    const authorRef = useRef();

    useEffect(() => {
        if (!authorRef.current) {
            return;
        }
        if (windowWidth < 970 && windowWidth > 904) {
            const offsetHeight = authorRef?.current.offsetTop;
            if (offsetHeight < backgroundHeight) {
                const padding = backgroundHeight - offsetHeight + 28;
                if (authorPaddingTop !== padding) {
                    setAuthorPaddingTop(padding);
                }
            }
        } else if (authorPaddingTop !== 28) {
            setAuthorPaddingTop(28);
        }
    }, [authorRef, backgroundHeight, windowWidth, authorPaddingTop]);

    return (
        <View
            display="flex"
            justifyContent="space-between"
            css={{
                a: {
                    textDecoration: 'none',
                    ':hover': {
                        color: `${colors.primary[60]} !important`,
                    },
                    ':visited': {
                        color: `${colors.primary[60]} !important`,
                    },
                },
            }}
        >
            <GridColumn
                size={{ xs: 12, sm: 12, md: 7.8, lg: 7.6 }}
                css={{ paddingLeft: '0px', paddingRight: '0px' }}
            >
                {contentDescription?.length ? (
                    <Heading
                        pt="24px"
                        variant="headingSm"
                        fontWeight="600"
                        fontSize={{ _: '16px', md: '20px' }}
                        lineHeight={{ _: '24px', md: '30px' }}
                        color={colors.neutral[100]}
                        as="h2"
                    >
                        {contentDescription}
                    </Heading>
                ) : null}
                {isSponsor && paidCompany?.companyName ? (
                    <Text
                        pt="24px"
                        as="p"
                        margin="0"
                        textAlign="left"
                        variant="labelLg"
                        color={colors.neutral[60]}
                    >
                        {showSponsoredBy && (
                            <>
                                Sponsored by{' '}
                                <Link
                                    href={`${paidCompany.companyUrl}`}
                                    variant="inline"
                                    target="_self"
                                >
                                    {paidCompany?.companyName}
                                </Link>
                            </>
                        )}
                    </Text>
                ) : null}
                <View
                    ref={articlePictureRef}
                    mt={{ _: '24px', sm: '32px', md: '48px' }}
                    overflow="hidden"
                    css={{
                        aspectRatio: '688 / 388',
                        '.component-Elements-Picture': {
                            display: 'block !important',
                            height: 'auto !important',
                        },
                        img: {
                            width: '100% !important',
                            height: 'auto !important',
                        },
                    }}
                >
                    <ResponsivePicture
                        breakpoints={breakpoints}
                        cloudinaryID={src}
                        alt={alt}
                        disableHeight
                        handleOnLoad={handleResize}
                        loading="eager"
                    />
                </View>
                {audioSrc ? <AudioPlayer src={audioSrc} /> : null}
                <Text
                    as="p"
                    color={colors.neutral[60]}
                    margin="0"
                    textAlign="left"
                    variant="labelSm"
                    pt="4px"
                >
                    {credit}
                </Text>
                <Author
                    pt={`${authorPaddingTop}px`}
                    author={author}
                    type={type}
                    hash={hash}
                    content={content}
                    contentModifiedDate={contentModifiedDate}
                    windowWidth={windowWidth}
                    ref={authorRef}
                />
            </GridColumn>

            <GridColumn
                size={{
                    xs: 0,
                    sm: 0,
                    md: windowWidth > 1054 ? 3 : 4,
                    lg: 3.05,
                }}
                pr={{ _: '0px', md: '24px', lg: '0px' }}
                css={{
                    paddingLeft: '0px',
                }}
            >
                {!isLoggedIn() ? (
                    <Convincer
                        onConvincerClick
                        windowWidth={windowWidth}
                        ref={topConvincerRef}
                    />
                ) : null}
            </GridColumn>
        </View>
    );
};

ContentInfo.displayName = 'ContentInfo';
